// Binary responses through the same auth the axios client sends. `<img src>`
// and `<a href>` cannot carry an Authorization header, so PDFs, PNGs and
// containers behind the embed API are fetched as blobs and handed to the
// browser as object URLs. Never camelized — bytes, not JSON.
import { useEffect, useState } from 'react';
import { auth } from './auth';
import { domain } from './request';

export async function fetchAuthedBlob(path, { headers = {} } = {}) {
  const base = auth().headers;
  const res = await fetch(`${domain}/${path.replace(/^\//, '')}`, {
    headers: { ...base, ...headers },
  });
  if (!res.ok) {
    let detail = null;
    try {
      detail = await res.json();
    } catch {
      /* not JSON */
    }
    const err = new Error(detail?.detail?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.detail = detail?.detail || null;
    throw err;
  }
  return res.blob();
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function openBlob(blob) {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// An object URL for an authenticated image/PDF, revoked on unmount or when
// the path changes. `null` while loading or when the fetch failed.
export function useAuthedObjectUrl(path, { headers, enabled = true } = {}) {
  const [url, setUrl] = useState(null);
  const headerKey = JSON.stringify(headers || {});
  useEffect(() => {
    if (!path || !enabled) {
      setUrl(null);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    fetchAuthedBlob(path, { headers: headers || {} })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled, headerKey]);
  return url;
}
